import { Injectable } from '@angular/core';
import { User } from '../shared/interfaces/interfaces';
import { ConfigurationService } from './configuration.service';
import * as Blockly from 'blockly';
import { Subject } from 'rxjs';
import { ToastMessageComponent } from 'src/app/shared/components/toast-message/toast-message.component';
import { MatSnackBar } from '@angular/material/snack-bar';
import { OTA_Status } from '../shared/enums/SequenceKeys.enum';
import dayjs from 'dayjs';

@Injectable({
  providedIn: 'root'
})
export class CommonService {

  constructor(
    private configService: ConfigurationService,
    private toastMessage: MatSnackBar,
  ) { }
  /**
  * Formats the profile name.
  * @param profile - The profile name to be formatted.
  */
 userDetails: User = {};
 blockDropdownClickSubject = new Subject<any>();
 blockDropdownClickEvent$ = this.blockDropdownClickSubject.asObservable();
  colorSet = ['#E95E6F', '#EF9453', '#F7C325', '#1AAE9F', '#92CA4B', '#897A5F',
    '#439AEB', '#9635E2', '#C660D7', '#4B5C6B', '#788896', '#BABABA'];

  colorPairs = [
    { bg: '#D5E3ED', text: '#1F6499' }, // Group 1
    { bg: '#E5CFD2', text: '#661422' }, // Group 2
    { bg: '#DFCFE5', text: '#501466' }, // Group 3
    { bg: '#D1CFE5', text: '#1D1466' }, // Group 4
    { bg: '#D1E5CF', text: '#206719' }, // Group 5
    { bg: '#DED1C8', text: '#663614' }  // Group 6
  ];

  alphabetGroups = [
    'ABCDE',   // Pair 0
    'FGHIJ',   // Pair 1
    'KLMNO',   // Pair 2
    'PQRST',   // Pair 3
    'UVWXY',   // Pair 4
    'Z'        // Pair 5
  ];

  getFormattedProfileName(profile: string) {
    let isCarrier = this.configService.getConfig('isCarrier') || false;
    switch (profile) {
      case 'SYSTEM_DEFAULT':
      case 'defaultSystemEquip':
        return 'Default System Profile';
      case 'SYSTEM_VAV_ANALOG_RTU':
      case 'vavFullyModulatingAhu':
          return 'VAV Fully Modulating AHU';
      case 'SYSTEM_VAV_STAGED_RTU':
      case 'vavStagedRtu':
          return 'VAV Staged RTU';
      case 'SYSTEM_VAV_HYBRID_RTU':
          return 'VAV Advanced Hybrid AHU';
      case 'SYSTEM_VAV_STAGED_VFD_RTU':
      case 'vavStagedRtuVfdFan':
      case 'vavStagedVfdRtu':
          return 'VAV Staged RTU with VFD Fan';
      case 'SYSTEM_DAB_ANALOG_RTU':
      case 'dabFullyModulatingAhu':
          return isCarrier ? 'VVT-C Fully Modulating AHU' : 'DAB Fully Modulating AHU';
      case 'SYSTEM_DAB_STAGED_RTU':
      case 'dabStagedRtu':
          return isCarrier ? 'VVT-C Staged RTU' : 'DAB Staged RTU';
      case 'SYSTEM_DAB_HYBRID_RTU':
          return isCarrier ? 'VVT-C Advanced Hybrid AHU' : 'DAB Advanced Hybrid AHU';
      case 'SYSTEM_DAB_STAGED_VFD_RTU':
      case 'dabStagedRtuVfdFan':
          return isCarrier ? 'VVT-C Staged RTU with VFD Fan' : 'DAB Staged RTU with VFD Fan';
      case 'dabExternalAHUController':
          return isCarrier ? 'VVT-C External AHU' : 'DAB External AHU';
      case 'vavExternalAHUController':
          return 'VAV External AHU';
      case 'vavAdvancedHybridAhuV2':
          return 'VAV Advanced Hybrid AHU v2';
      case 'dabAdvancedHybridAhuV2':
          return isCarrier? 'VVT-C Advanced Hybrid AHU v2' : 'Dab Advanced Hybrid AHU v2';
      default:
          return profile;
    }
  }

  getFormattedColumnName(columnName: string) {
    switch (columnName) {
      case 'deliveredDateTime':
        return 'Delivered Date & Time';
      case 'ackedDateTime':
        return 'Confirmed Receipt';
      case 'ccuName':
        return 'System';
      case 'requestTime':
          return 'Request Time';
      default:
        return columnName;
    }
  }

  getColumnNameForCCUDeliveryLogs(columnName: string) {
    switch (columnName) {
      case 'status':
        return 'Status';
      case 'ccuZoneModules':
        return 'CCU/Zone Modules';
      case 'deliveryDateTime':
        return 'Delivery Date & Time';
      case 'confirmedReceipt':
        return 'Confirmed Receipt';
      case 'debugInformation':
        return 'Debug Information';
      default:
        return columnName;
    }
  }

  getFormattedKeyForAlertBlock(key: string) {
    switch (key) {
      case 'ALERT_TITLE': 
        return 'title';
      case 'ALERT_MESSAGE':
        return 'message';
      case 'ALERT_SEVERITY':
        return 'severity';
      case 'ALERT_TYPE':
        return 'alertType';
      case 'ALERT_FIX_OFFSET':
        return 'fixOffset';
      case 'ALERT_TRIGGER_OFFSET':
        return 'triggerOffset';
      case 'SHOW_LOWER_LIMIT': 
        return 'lowerLimitEnabled';
      case 'SHOW_UPPER_LIMIT':
        return 'upperLimitEnabled';
      case 'ALERT_TRIGGER_UPPER_LIMIT_OFFSET':
        return 'upperLimitTriggerOffset';
      case 'ALERT_FIX_UPPER_LIMIT_OFFSET':
        return 'upperLimitFixOffset';
      case 'ALERT_TRIGGER_LOWER_LIMIT_OFFSET':
        return 'lowerLimitTriggerOffset';
      case 'ALERT_FIX_LOWER_LIMIT_OFFSET':
        return 'lowerLimitFixOffset';
      case 'alertWithOffset':
        return 'ALERT_BLOCK_OFFSET';
      case 'alertHystersis':
        return 'ALERT_BLOCK_OFFSET_WITH_LIMIT';
      default:
        return key;
    }
  }

  getDisplayId() {
    const loggedInUserDetails = this.userDetails;
    if (Object.keys(loggedInUserDetails)?.length) {
      return loggedInUserDetails.firstName + ' ' + loggedInUserDetails.lastName;
    } else {
      return null;
    }
  }
  
  /**
   * Method to subscribe to the dropdown click event on the block
   * @param targetBlock - Target block on which the dropdown click event needs to be subscribed.
   * @param targetFieldName - Name of the dropdown field on a block 
   * @param hideMenuOnclick - boolean value to hide the dropdown menu on click
   */
  subcribeToScopeDropdownClick(targetBlock: any, targetFieldName: string, hideMenuOnclick: boolean) {
    const dropdownField = targetBlock.getField(targetFieldName);
    const fieldGroup = dropdownField?.fieldGroup_; // fieldGroup_ is SVG element that represents the dropdown in workspace.
  
    if (!fieldGroup || fieldGroup.dataset.listenerBound) return;
    fieldGroup.dataset.listenerBound = 'true';
  
    // Disables blockly's default dropdown by replacing the function that shows the dropdown menu (showEditor_) with an empty function
    dropdownField.showEditor_ = function () {
      this.sourceBlock_.workspace.hideChaff();
    };

    // Adds a new event listener to the fieldGroup element that listens for pointerdown events (clicks) and triggers the dropdown click event
    fieldGroup.addEventListener('pointerdown', (event: PointerEvent) => {
      event.preventDefault(); // To prevent the default behavior.
      event.stopImmediatePropagation(); // Blocks all other event listeners from being triggered, including Blockly's internal.
      if (hideMenuOnclick) {
        Blockly.hideChaff(); // closes any open Blockly dropdowns/tooltips
      }
      this.blockDropdownClickSubject.next({ targetBlock, targetFieldName }); // Emit the event with the target block and field name
    });
  }

  /**
   * Method to set the dropdown options dynamically by using provided fieldName and provided blockName
   * @param targetBlock - Target block in which the dropdown options needs to be updated.
   * @param workspace - Workspace object
   * @param fieldName - Name of the dropdown field on a block 
   * @param options - Dropdown options array [{label: '', value: ''}, {label: '', value: ''}]
   * @param isoptionsCopyRequired - boolen value to save the copy of provided options in the fieldName_copy property isnide the targetblock
   */
  setDropdownOptionsForBlock(targetBlock: any, workspace: any, fieldName: any, options: any, isoptionsCopyRequired: boolean) {
    let optionsFieldValues = targetBlock.getField(fieldName).getOptions(); // Getting the existing options of the dropdown field of the test block.
    // Setting the options to the dropdown field of the test block.
    options.forEach((option: any, index: any) => {
      optionsFieldValues[index] = [option.label, option.value]; 
    });
    optionsFieldValues[options.length] ? optionsFieldValues.splice(options.length, optionsFieldValues.length - options.length) : ''; // Removing the extra options from the dropdown field of the test block.
    workspace.render();
    isoptionsCopyRequired ? targetBlock[`${fieldName}_copy`] = options : ''; // saving the copy of options array inside the block object to avoid fetching the options from API again.
  }

  // Toast Message 
  showToast(type: string, message: string) {
    this.toastMessage.openFromComponent(ToastMessageComponent, {
      duration: 5000,
      data: {
        type: type,
        message: message
      },
    });
  }

  getStatusIconPath(status: string) {
    status = status?.includes('OTA Error') ? OTA_Status.OTA_ERROR : status;
    switch (status) {
      case OTA_Status.OTA_RECIEVED:
      case OTA_Status.OTA_Delivered:
        return 'assets/images/status-success.svg';
      case OTA_Status.OTA_ERROR:
        return 'assets/images/status-error.svg';
      case OTA_Status.OTA_IN_PROGRESS:
        return 'assets/images/status-warning.svg';
      default:
        return 'assets/images/status-warning.svg';
    }
  }

  /**
   * Corrects device-specific wording in OTA messages returned by the API.
   * PCN messages already use the protocol-converter label; custom-controller
   * devices must use their own label instead.
   */
  getDeviceOtaStatus(status: string | undefined, deviceName: string | undefined): string | undefined {
    if (!status) {
      return status;
    }

    const normalizedDeviceName = deviceName ?? '';
    if (/custom\s*controller/i.test(normalizedDeviceName)) {
      return status.replace(/protocol convert(?:er|or) node/gi, 'Custom Controller Node');
    }

    if (/pcn/i.test(normalizedDeviceName)) {
      return status.replace(/protocol convert(?:er|or) node/gi, 'Protocol Converter Node');
    }

    return status;
  }

  /**
   * Method to get the model name with version from the modelConfig object.
   * @param modelConfig - Model configuration object containing modelName and modelVersion.
   * @returns Formatted string with model name and version. (e.g., "ModelName (v 1.0.0)")
   */
  getModelNameWithVersion(modelConfig: any) {
    return modelConfig?.modelName ? `${modelConfig.modelName}${modelConfig.modelVersion ? ` (v ${modelConfig.modelVersion})` : ''}` : '';
  }

  getAvatarColors(firstName: string) {
    if (!firstName) {
      return this.colorPairs[0]; // default fallback
    }

    const initial = firstName.charAt(0).toUpperCase();

    const groupIndex = this.alphabetGroups.findIndex(group =>
      group.includes(initial)
    );

    return this.colorPairs[groupIndex !== -1 ? groupIndex : 0];
  }

  /**
   * Method to compute initials from first and last name
   * @param firstName - The first name of the user
   * @param lastName - The last name of the user
   * @returns The initials in uppercase
   */
  computeInitials(firstName: string, lastName: string) {
    const f = firstName?.trim() || '';
    const l = lastName?.trim() || '';
  
    return ((f.charAt(0) || '') + (l.charAt(0) || '')).toUpperCase();
  }

  /**
   * Method to get avatar initials and colors based on first and last name
   * @param firstName - The first name of the user
   * @param lastName - The last name of the user
   * @returns {initials: string, colors: any} An object containing the initials and corresponding avatar colors
   */
  getAvatarInitialsAndColors(firstName: string, lastName: string) {
    const formattedFirstName = (firstName ?? '').trim();
    const formattedLastName  = (lastName ?? '').trim();
  
    const userInitials = this.computeInitials(formattedFirstName, formattedLastName);
  
    if(!userInitials) {
      return { initials: '', colors: null };
    }
    
    const nameForColor = formattedFirstName || formattedLastName;
    const avatarColors =  this.getAvatarColors(nameForColor);
    return { initials: userInitials, colors: avatarColors };
  }

  formatDate(date: string | Date): string {
    return dayjs.utc(date).local().format('MMM D, YYYY | h:mm A');
  }
  // Methods related to virtual accordion sections and lists
  buildVirtualAccordionSection(
    options: {
      key: string;
      title: string;
      isOpen: boolean;
      items?: any[];
      errorMessage?: string;
    }
  ): any[] {
    const list: any[] = [];

    list.push({
      type: 'header',
      key: options.key,
      title: options.title,
      isOpen: options.isOpen
    });

    if (!options.isOpen) {
      return list;
    }

    if (!options?.items || options.items.length === 0) {
      list.push({
        type: 'empty',
        key: options.key,
        message: options.errorMessage || 'No exact match found.'
      });
      return list;
    }

    for (const item of options.items) {
      list.push({
        type: 'item',
        key: options.key,
        data: item
      });
    }

    return list;
  }

  // Method to build a virtual accordion list from multiple sections
  buildVirtualAccordionList(sections: Array<{
    key: string;
    title: string;
    isOpen: boolean;
    items?: any[];
    errorMessage?: string;
  }>): any[] {
    const list: any[] = [];
    (sections || []).forEach((section: any) => {
      list.push(...this.buildVirtualAccordionSection(section));
    });
    return list;
  }

  // Method to calculate the virtual viewport height for the accordion content
  getVirtualViewportHeight(
    isPersonalOpen: boolean,
    isSharedOpen: boolean,
    itemCount: number,
    maxHeight: number = 370,
    minHeight: number = 56,
    itemRowHeight: number = 26
  ): number {
    if (!isPersonalOpen && !isSharedOpen) {
      return minHeight;
    }

    const contentHeight = itemCount * itemRowHeight;
    return Math.max(minHeight, Math.min(contentHeight, maxHeight));
  }

  /**
   * Toggles visibility for a dialog panel and its corresponding backdrop using a panel class selector.
   * @param panelClass - Dialog panel class used in MatDialog config.
   * @param dialogRef - Optional MatDialogRef-like object for panel class toggling.
   * @param hide - True to hide, false to restore.
   * @param panelHiddenClass - CSS class used to hide the panel.
   * @param backdropHiddenClass - CSS class used to hide the backdrop.
   */
  setDialogWithBackdropVisibilityByPanelClass(
    panelClass: string,
    dialogRef?: { addPanelClass?: (className: string) => void; removePanelClass?: (className: string) => void } | null,
    hide: boolean = true,
    panelHiddenClass: string = 'mat-dialog-hidden',
    backdropHiddenClass: string = 'dialog-backdrop-hidden'
  ): void {
    if (!panelClass) {
      return;
    }

    if (hide) {
      dialogRef?.addPanelClass?.(panelHiddenClass);
    } else {
      dialogRef?.removePanelClass?.(panelHiddenClass);
    }

    const overlayPane = document.querySelector(`.cdk-overlay-pane.${panelClass}`) as HTMLElement | null;
    const backdrop = overlayPane
      ?.parentElement
      ?.parentElement
      ?.querySelector('.cdk-overlay-backdrop') as HTMLElement | null;

    if (hide) {
      backdrop?.classList.add(backdropHiddenClass);
    } else {
      backdrop?.classList.remove(backdropHiddenClass);
    }
  }

  validCertificationLevels = ['L0', 'L1', 'L2', 'L3'];

  /**
   * Method to get the display label for a user's certification level (matches internal's user-management-layout wording).
   * @param certificationLevel - The certification level (e.g. 'L0', 'L1', 'L2', 'L3').
   */
  getCertificationLevelLabel(certificationLevel: string | null): string {
    const formattedCertificationLevel = (certificationLevel ?? '').toUpperCase();
    switch (formattedCertificationLevel) {
      case 'L0':
        return 'Non-Certified';
      case 'L1':
        return 'L1 Certified';
      case 'L2':
        return 'L2 Certified';
      case 'L3':
        return 'L1 Certified Integrator';
      case 'F0':
        return '';
      case 'F1':
        return '';
      default:
        return '';
    }
  }

  /**
   * Method to check whether a certification level is one of the valid installer levels (L0, L1, L2, L3).
   * @param certificationLevel - The certification level to validate (case-insensitive, to tolerate backend casing variance).
   */
  isValidCertificationLevel(certificationLevel: string | null | undefined): boolean {
    const formattedCertificationLevel = (certificationLevel ?? '').toUpperCase();
    return this.validCertificationLevels.includes(formattedCertificationLevel);
  }

}
